import { useId } from "react";
import { ChevronDown } from "lucide-react";

/**
 * PlanMoreFolds — issue #1164 (D219, D254, board PlanGoals "More"): the rarely
 * used parts of Plan as closed folds, one open at a time. Same open/close
 * contract as `AccordionCard` (a `button` with `aria-expanded` and
 * `aria-controls`, the body unmounted while closed), and no `aria-haspopup`,
 * so journey 60's G1 does not count a fold as a place to go (D224).
 *
 * Which fold is open is the caller's: the page reads it from the URL
 * (`?tab=<fold>`) and `onToggle(key)` writes it back, so a notification or an
 * old bookmark opens the right one.
 *
 * `folds = [{ key, title, sub, body }]`. Test ids: `plan-more-<key>` on the
 * trigger, `plan-more-<key>-region` on the open body.
 */
export function PlanMoreFolds({ folds, openKey, onToggle }) {
  const base = useId();
  return (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="plan-more">
      {folds.map((f) => {
        const open = f.key === openKey;
        const regionId = `${base}-${f.key}`;
        return (
          <div
            key={f.key}
            style={{ background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: 12, overflow: "hidden" }}
          >
            <button
              type="button"
              onClick={() => onToggle(f.key)}
              aria-expanded={open}
              aria-controls={regionId}
              data-testid={`plan-more-${f.key}`}
              className="w-full text-left flex items-center min-w-0"
              style={{ gap: 10, minHeight: 56, padding: "10px 14px", background: "none", border: "none", cursor: "pointer" }}
            >
              <span className="flex flex-col min-w-0" style={{ gap: 2, flexGrow: 1 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--nw-ink)" }}>{f.title}</span>
                {f.sub && <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>{f.sub}</span>}
              </span>
              <ChevronDown
                size={16}
                strokeWidth={2.5}
                color="var(--nw-navy)"
                aria-hidden="true"
                style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
              />
            </button>
            {open && (
              <div id={regionId} role="region" aria-label={typeof f.title === "string" ? f.title : f.key} data-testid={`plan-more-${f.key}-region`} style={{ padding: "4px 14px 14px" }}>
                {f.body}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default PlanMoreFolds;
